// IncomingDrawer — full PO drawer for the new Incoming tab.
//
// Mo dropped the 5-stage timeline as fluff, so the drawer is simpler
// than the prototype:
//   - Receipt progress (cumulative across the new forecaster_receipts
//     ledger; bar fills as units arrive)
//   - "Log receipt" form  → writeReceipt() in stockWrites.js
//                          (writes the forecaster_receipts ledger row +
//                          mirrors legacy received_qty/at/by; does NOT
//                          touch SOH — stock on hand is manual-only)
//   - Typed attachments — logistics docs (packing slip / POD / invoice /
//     photo) + compliance docs (COA / compliance cert / batch cert /
//     other report); upload to existing `po-attachments` bucket, log via
//     writeIncomingAttachment(); view / download / delete inline
//   - Read-only meta grid: qty ordered, supplier, ETA, PO ref, unit
//     cost, PO value
//
// The existing inline "Mark as Received" button on the table stays as
// a quick action — both routes through the same writeReceipt helper.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { storage } from '../lib/awsStorage';
import { writeReceipt, writeIncomingAttachment, closeIncoming, reopenIncoming } from './stockWrites';
import FilePreviewModal from '../po/FilePreviewModal';

const SERIF = '\'Tiempos\', Georgia, \'Times New Roman\', serif';
const SANS  = 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif';
const MONO  = '\'JetBrains Mono\', ui-monospace, \'SF Mono\', Menlo, monospace';

const ATTACHMENT_KINDS = [
  { id: 'packing_slip', label: 'Packing slip', icon: '📋' },
  { id: 'pod',          label: 'Proof of delivery', icon: '✅' },
  { id: 'invoice',      label: 'Supplier invoice', icon: '🧾' },
  { id: 'photo',        label: 'Photo / damage', icon: '📷' },
  // Phase 2 — batch-level compliance docs (mig 142 widened the CHECK).
  // Keep ids byte-identical with stockWrites.js ATTACHMENT_KINDS and the
  // migration CHECK or writeIncomingAttachment() throws / inserts reject.
  { id: 'compliance_cert', label: 'Compliance Certificate', icon: '📜' },
  { id: 'coa',             label: 'COA', icon: '🧪' },
  { id: 'batch_cert',      label: 'Batch Certificate', icon: '🏷️' },
  { id: 'other_report',    label: 'Other report', icon: '📄' },
];

function fmtMoney(n) { return `A$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`; }
function fmtShortDate(ts) {
  if (!ts) return '—';
  // Always include the year — Ace flagged 2026-05-02 that "31 May" with no
  // year is ambiguous on the Stock by-PO view (could be this year or next).
  return new Date(ts).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtRelative(ts) {
  if (!ts) return '';
  const ms = Date.now() - new Date(ts).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m || '<1'}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 14) return `${d}d ago`;
  return fmtShortDate(ts);
}

// Resolve a stored incoming-attachment path to a key awsStorage can sign
// or delete. Tolerates the legacy raw key ('incoming/...'), an already
// bucket-prefixed key ('po-attachments/...'), and the s3:-prefixed form.
function attObjectKey(path) {
  if (!path) return path;
  const p = path.startsWith('s3:') ? path.slice(3) : path;
  return p.startsWith('po-attachments/') ? p : `po-attachments/${p}`;
}

export default function IncomingDrawer({
  session, po, sku, catalog = [],
  onClose, onSaved,
}) {
  const cat = useMemo(
    () => sku?.catalog_id ? (catalog.find(c => c.id === sku.catalog_id) || null) : null,
    [sku, catalog],
  );

  const [receipts, setReceipts] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [receiveQty, setReceiveQty] = useState('');
  const [receiveNote, setReceiveNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [preview, setPreview] = useState(null);
  const [paid, setPaid] = useState(!!po?.paid);
  const [paidBusy, setPaidBusy] = useState(false);
  const [disposition, setDisposition] = useState('more_coming'); // partial-receipt: 'more_coming' | 'final' | 'dispute'

  const reload = useCallback(async () => {
    if (!po?.id) return;
    setLoading(true);
    const [recRes, attRes] = await Promise.all([
      aws.from('forecaster_receipts').select('*').eq('incoming_id', po.id).order('received_at', { ascending: false }),
      aws.from('forecaster_incoming_attachments').select('*').eq('incoming_id', po.id).order('uploaded_at', { ascending: false }),
    ]);
    setReceipts(recRes.data || []);
    setAttachments(attRes.data || []);
    setLoading(false);
  }, [po?.id]);

  useEffect(() => { reload(); }, [reload]);

  // Default the receive qty to the open balance when the drawer first opens.
  useEffect(() => {
    if (!po) return;
    setReceiveQty('');
    setReceiveNote('');
    setJustSaved(false);
    setPaid(!!po.paid);
    setDisposition('more_coming');
  }, [po?.id]);

  // Esc to close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!po) return null;

  const orderedQty = Number(po.qty || 0);
  // Source of truth = forecaster_receipts ledger; fall back to legacy
  // received_qty for any rows that pre-date migration 83.
  const ledgerSum = receipts.reduce((s, r) => s + Number(r.qty || 0), 0);
  const totalReceived = ledgerSum > 0 ? ledgerSum : Number(po.received_qty || 0);
  const remaining = Math.max(0, orderedQty - totalReceived);
  const pct = orderedQty > 0 ? Math.min(100, Math.round((totalReceived / orderedQty) * 100)) : 0;
  const closed = remaining === 0 && orderedQty > 0;

  const etaMs = po.expected_date ? new Date(po.expected_date).getTime() - Date.now() : null;
  const etaDays = etaMs != null ? Math.ceil(etaMs / 86400000) : null;
  const etaTone = etaDays == null ? '#6E7278' : etaDays < 0 ? '#C2410C' : etaDays < 7 ? '#0F8E6F' : '#0F1419';

  const unitCost = cat?.unit_cost != null ? Number(cat.unit_cost) : null;
  const poValue = unitCost != null ? unitCost * orderedQty : null;

  async function logReceipt() {
    if (busy || !sku) return;
    const qty = parseInt(receiveQty) || 0;
    if (qty <= 0) return;
    const isPartial = qty < remaining;
    setBusy(true);
    try {
      await writeReceipt(session, {
        po,
        sku,
        qty,
        isPartial,
        note: receiveNote.trim() || null,
      });
      // Phase 2.1 disposition — only acts on a PARTIAL receipt. None of
      // these touch SOH. 'more_coming' (default) leaves the row open.
      if (isPartial && disposition === 'final') {
        await closeIncoming(session, { incomingId: po.id, reason: 'short_received', skuName: sku.sku_name });
      } else if (isPartial && disposition === 'dispute') {
        await authWrite(session, 'forecaster_incoming_stock', 'update', {
          dispute_reason: receiveNote.trim() || 'Flagged at receipt',
          disputed_at: new Date().toISOString(),
        }, { column: 'id', value: po.id });
        await aws.from('forecaster_activity').insert([{
          actor_name: session.name,
          action: 'Incoming disputed (at receipt)',
          detail: `${sku.sku_name} (PO ${po.po_ref || po.id})`,
        }]);
      }
      setReceiveQty('');
      setReceiveNote('');
      setDisposition('more_coming');
      setJustSaved(true);
      onSaved?.();
      await reload();
      setTimeout(() => setJustSaved(false), 1400);
    } catch (err) {
      console.error('[IncomingDrawer] receipt failed', err);
      alert('Receipt failed: ' + (err?.message || 'unknown error'));
    } finally {
      setBusy(false);
    }
  }

  async function handleReopen() {
    if (busy) return;
    setBusy(true);
    try {
      await reopenIncoming(session, { incomingId: po.id, skuName: sku?.sku_name });
      onSaved?.();
      await reload();
    } catch (err) {
      console.error('[IncomingDrawer] reopen failed', err);
      alert('Reopen failed: ' + (err?.message || 'unknown error'));
    } finally {
      setBusy(false);
    }
  }

  async function pickAttachment(kind) {
    if (busy) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    input.onchange = async (e) => {
      const file = e.target?.files?.[0];
      if (!file) return;
      setBusy(true);
      try {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const key = `incoming/${po.id}/${kind}/${Date.now()}-${safeName}`;
        const { error: upErr } = await storage.from('po-attachments').upload(key, file, { contentType: file.type });
        if (upErr) throw upErr;
        await writeIncomingAttachment(session, { incomingId: po.id, kind, path: key, batchNumber: po.batch_number || null });
        await reload();
      } catch (err) {
        console.error('[IncomingDrawer] upload failed', err);
        alert('Upload failed: ' + (err?.message || 'unknown error'));
      } finally {
        setBusy(false);
      }
    };
    input.click();
  }

  async function viewAttachment(a) {
    try {
      const { data, error } = await storage.from('po-attachments').createSignedUrl(attObjectKey(a.path));
      if (error || !data?.signedUrl) { alert('Could not open file'); return; }
      setPreview({ url: data.signedUrl, filename: (a.path || '').split('/').pop() });
    } catch (err) {
      console.error('[IncomingDrawer] view failed', err);
      alert('Could not open file: ' + (err?.message || 'unknown error'));
    }
  }

  async function deleteAttachment(a) {
    if (busy) return;
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    setBusy(true);
    try {
      await authWrite(session, 'forecaster_incoming_attachments', 'delete', null, { column: 'id', value: a.id });
      await storage.from('po-attachments').remove([attObjectKey(a.path)]);
      await aws.from('forecaster_activity').insert([{
        actor_name: session.name,
        action: 'Incoming document removed',
        detail: `${a.kind.replace(/_/g, ' ')} · ${(a.path || '').split('/').pop()}`,
      }]);
      await reload();
    } catch (err) {
      console.error('[IncomingDrawer] delete failed', err);
      alert('Delete failed: ' + (err?.message || 'unknown error'));
    } finally {
      setBusy(false);
    }
  }

  async function togglePaid() {
    if (paidBusy) return;
    const next = !paid;
    setPaidBusy(true);
    try {
      await authWrite(session, 'forecaster_incoming_stock', 'update', {
        paid: next,
        paid_at: next ? new Date().toISOString() : null,
        paid_by: next ? session.name : null,
      }, { column: 'id', value: po.id });
      await aws.from('forecaster_activity').insert([{
        actor_name: session.name,
        action: next ? 'Incoming marked paid (reference)' : 'Incoming paid flag cleared',
        detail: `${sku?.sku_name || po.po_ref || po.id}${po.po_ref ? ' · PO ' + po.po_ref : ''}`,
      }]);
      setPaid(next);
      onSaved?.();
    } catch (err) {
      console.error('[IncomingDrawer] paid toggle failed', err);
      alert('Could not update paid flag: ' + (err?.message || 'unknown error'));
    } finally {
      setPaidBusy(false);
    }
  }

  return createPortal(
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 900,
        background: 'rgba(15,20,25,0.32)', backdropFilter: 'blur(2px)',
      }} />
      <aside style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 100vw)',
        background: '#FAFAF8', zIndex: 1000,
        display: 'flex', flexDirection: 'column', fontFamily: SANS,
        borderLeft: '1px solid #E8E6E0', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 26px 14px', borderBottom: '1px solid #E8E6E0',
          display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{
              fontFamily: SERIF, fontSize: 22, fontWeight: 400,
              color: '#0F1419', margin: 0, letterSpacing: '-0.01em',
            }}>{po.po_ref || 'Untitled PO'}</h2>
            <div style={{
              fontFamily: MONO, fontSize: 11, color: '#6E7278',
              marginTop: 4, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
            }}>
              <span>{cat?.sku_code || '—'}</span>
              <span style={{ color: '#D5D3CD' }}>·</span>
              <span>{sku?.sku_name || '—'}</span>
              <span style={{ color: '#D5D3CD' }}>·</span>
              <span style={{
                color: closed ? '#9A9DA1' : '#0F8E6F',
                fontWeight: 500,
              }}>{closed ? 'Closed' : 'Open'}</span>
              {paid && (<>
                <span style={{ color: '#D5D3CD' }}>·</span>
                <span
                  title="Paid — reference flag only, not Finance payment status"
                  style={{ color: '#0F8E6F', fontWeight: 500 }}
                >PAID (ref)</span>
              </>)}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: '#6E7278', padding: 6, borderRadius: 6, lineHeight: 1,
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '22px 26px 26px' }}>
          {/* Progress + log receipt */}
          <ProgressBlock
            orderedQty={orderedQty}
            totalReceived={totalReceived}
            remaining={remaining}
            pct={pct}
            etaDays={etaDays}
            etaTone={etaTone}
          />

          <PaidToggle paid={paid} busy={paidBusy} onToggle={togglePaid} />

          {po.closed_at ? (
            <ReopenBlock onReopen={handleReopen} reason={po.close_reason} busy={busy} />
          ) : !closed && (
            <LogReceiptForm
              remaining={remaining}
              receiveQty={receiveQty}
              setReceiveQty={setReceiveQty}
              receiveNote={receiveNote}
              setReceiveNote={setReceiveNote}
              disposition={disposition}
              setDisposition={setDisposition}
              busy={busy}
              justSaved={justSaved}
              onSubmit={logReceipt}
            />
          )}

          {/* Typed attachments */}
          <AttachmentsBlock
            attachments={attachments}
            onPick={pickAttachment}
            onView={viewAttachment}
            onDelete={deleteAttachment}
            busy={busy}
          />

          {/* Receipt history */}
          {receipts.length > 0 && (
            <div style={{ marginTop: 22 }}>
              <div style={{ ...labelStyle, marginBottom: 8 }}>Receipt history</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {receipts.map(r => (
                  <div key={r.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 12px',
                    background: '#fff', border: '1px solid #E8E6E0', borderRadius: 8,
                    fontSize: 12.5,
                  }}>
                    <span style={{ flex: '0 0 auto', fontFamily: MONO, fontWeight: 500, color: '#0F8E6F' }}>
                      +{Number(r.qty || 0).toLocaleString()}
                    </span>
                    {r.is_partial && (
                      <span style={{
                        fontSize: 10, padding: '1px 6px', borderRadius: 999,
                        background: '#FEF3C7', color: '#92400E', fontWeight: 500,
                      }}>partial</span>
                    )}
                    <span style={{ flex: 1, color: '#6E7278' }}>{r.note || ''}</span>
                    <span style={{ fontFamily: MONO, fontSize: 10, color: '#9A9DA1', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      {fmtRelative(r.received_at)} · {r.received_by || '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Editable details — qty / ETA / TR / PS / batch / expiry /
              warehouse / notes. Save commits via authWrite and writes
              a diff into forecaster_activity so every change is in the
              audit trail. Read-only meta grid stays underneath as the
              quick reference. */}
          <EditDetailsBlock
            session={session}
            po={po}
            sku={sku}
            onSaved={() => { onSaved?.(); }}
          />

          {/* Read-only meta grid */}
          <MetaBlock
            orderedQty={orderedQty}
            supplier={cat?.supplier_name}
            eta={po.expected_date}
            poRef={po.po_ref}
            unitCost={unitCost}
            poValue={poValue}
          />

          {/* Per-SKU activity history. Mirrors what OnHandDrawer +
              CatalogueDrawer already surface on their History tabs.
              Inlined here (no tab gate) because Mo's standard is
              "every SKU mention shows its history" — the master
              timeline still lives on the Catalogue drawer. */}
          <SkuActivityHistory sku={sku} cat={cat} />

          {loading && receipts.length === 0 && attachments.length === 0 && (
            <div style={{ ...emptyStyle, marginTop: 20 }}>Loading receipt history…</div>
          )}
        </div>
      </aside>
      {preview && (
        <FilePreviewModal
          url={preview.url}
          filename={preview.filename}
          onClose={() => setPreview(null)}
        />
      )}
    </>,
    document.body,
  );
}

function ProgressBlock({ orderedQty, totalReceived, remaining, pct, etaDays, etaTone }) {
  return (
    <div style={{
      padding: '16px 18px', borderRadius: 12,
      background: '#fff', border: '1px solid #E8E6E0',
      marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
        <span style={labelStyle}>Receipt progress</span>
        <span style={{ flex: 1 }} />
        {etaDays != null && (
          <span style={{
            fontFamily: MONO, fontSize: 11, color: etaTone, fontWeight: 500,
          }}>
            ETA {etaDays < 0 ? `${Math.abs(etaDays)}d late` : etaDays === 0 ? 'today' : `in ${etaDays}d`}
          </span>
        )}
      </div>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8,
        fontFamily: SERIF, fontSize: 26, color: '#0F1419', letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums', lineHeight: 1.1,
      }}>
        <span style={{ color: pct === 100 ? '#0F8E6F' : '#0F1419' }}>{totalReceived.toLocaleString()}</span>
        <span style={{ fontSize: 14, color: '#9A9DA1' }}>/ {orderedQty.toLocaleString()} units</span>
      </div>
      <div style={{ marginTop: 12, height: 6, background: '#EFEDE6', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: pct === 100 ? '#0F8E6F' : '#0F1419',
          transition: 'width 0.3s',
        }} />
      </div>
      <div style={{ marginTop: 6, fontSize: 11, color: '#6E7278' }}>
        {remaining > 0 ? `${remaining.toLocaleString()} remaining` : 'Fully received'}
      </div>
    </div>
  );
}

function ReopenBlock({ onReopen, reason, busy }) {
  return (
    <div style={{
      padding: '14px 16px', borderRadius: 12, marginBottom: 16,
      background: '#FBF6EF', border: '1px solid #EAD9C0',
    }}>
      <div style={{ fontSize: 12.5, color: '#92400E', marginBottom: 8 }}>
        Closed as {reason === 'short_received' ? 'short-received (final)' : (reason || 'closed')}. Variance preserved; receiving is locked.
      </div>
      <button onClick={onReopen} disabled={busy} style={{
        padding: '8px 14px', borderRadius: 8, border: '1px solid #EAD9C0', background: '#fff',
        fontSize: 12.5, fontWeight: 500, color: '#92400E', fontFamily: 'inherit',
        cursor: busy ? 'default' : 'pointer',
      }}>{busy ? '…' : 'Reopen'}</button>
    </div>
  );
}

function LogReceiptForm({
  remaining, receiveQty, setReceiveQty, receiveNote, setReceiveNote,
  disposition, setDisposition, busy, justSaved, onSubmit,
}) {
  const qtyN = parseInt(receiveQty) || 0;
  const dirty = qtyN > 0;
  const isPartial = qtyN > 0 && qtyN < remaining;
  return (
    <div style={{
      padding: '14px 16px', borderRadius: 12,
      background: '#fff', border: '1px solid #E8E6E0',
      marginBottom: 16,
    }}>
      <div style={{ ...labelStyle, marginBottom: 10 }}>Log a receipt</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input
          type="number" min={1} max={remaining}
          placeholder={`up to ${remaining.toLocaleString()}`}
          value={receiveQty}
          onChange={(e) => setReceiveQty(e.target.value)}
          style={{ ...inputStyle, flex: 1, fontVariantNumeric: 'tabular-nums' }}
        />
        <button onClick={() => setReceiveQty(String(remaining))} style={{
          padding: '8px 12px', borderRadius: 7, flexShrink: 0,
          border: '1px solid #E8E6E0', background: '#fff',
          fontSize: 11.5, color: '#6E7278', cursor: 'pointer',
          fontFamily: MONO, letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>Full</button>
      </div>
      <input
        value={receiveNote}
        placeholder="Note (optional)"
        onChange={(e) => setReceiveNote(e.target.value)}
        style={{ ...inputStyle, marginBottom: 10 }}
      />
      {isPartial && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: '#6E7278', marginBottom: 6 }}>
            Partial — {Math.max(0, remaining - qtyN).toLocaleString()} would remain. What next?
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { id: 'more_coming', label: 'More coming' },
              { id: 'final',       label: 'Final / short' },
              { id: 'dispute',     label: 'Dispute' },
            ].map(opt => (
              <button
                key={opt.id} type="button"
                onClick={() => setDisposition(opt.id)}
                style={{
                  flex: 1, padding: '7px 8px', borderRadius: 7, fontSize: 11.5, cursor: 'pointer',
                  fontFamily: 'inherit',
                  border: `1px solid ${disposition === opt.id ? '#0F1419' : '#E8E6E0'}`,
                  background: disposition === opt.id ? '#0F1419' : '#fff',
                  color: disposition === opt.id ? '#fff' : '#6E7278',
                }}
              >{opt.label}</button>
            ))}
          </div>
          {disposition === 'final' && (
            <div style={{ fontSize: 10.5, color: '#92400E', marginTop: 5 }}>
              Closes this PO short — remaining {Math.max(0, remaining - qtyN).toLocaleString()} recorded as variance. Reopenable.
            </div>
          )}
        </div>
      )}
      <button
        disabled={!dirty || busy}
        onClick={onSubmit}
        style={{
          width: '100%', padding: '10px 16px', borderRadius: 9,
          background: justSaved ? '#0F8E6F'
            : dirty ? '#0F1419' : 'rgba(15,20,25,0.08)',
          color: justSaved || dirty ? '#fff' : '#9A9DA1',
          border: 'none', cursor: dirty && !busy ? 'pointer' : 'default',
          fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        {justSaved ? (
          <>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7L6 11L12 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Receipt logged
          </>
        ) : busy ? 'Logging…' : (
          <>
            Log receipt
            {dirty && <span style={{ opacity: 0.7, fontFamily: MONO, fontSize: 11 }}>+{qtyN.toLocaleString()} units</span>}
          </>
        )}
      </button>
    </div>
  );
}

function AttachmentsBlock({ attachments, onPick, onView, onDelete, busy }) {
  // Group attachments by kind for the chip count.
  const byKind = useMemo(() => {
    const m = {};
    attachments.forEach(a => { m[a.kind] = (m[a.kind] || 0) + 1; });
    return m;
  }, [attachments]);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ ...labelStyle, marginBottom: 8 }}>Documents</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 10 }}>
        {ATTACHMENT_KINDS.map(k => {
          const count = byKind[k.id] || 0;
          return (
            <button
              key={k.id}
              onClick={() => onPick(k.id)}
              disabled={busy}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 10,
                background: '#fff', border: '1px solid #E8E6E0',
                cursor: busy ? 'default' : 'pointer', textAlign: 'left',
                fontFamily: 'inherit', fontSize: 12.5, color: '#0F1419',
              }}
            >
              <span style={{ fontSize: 16 }}>{k.icon}</span>
              <span style={{ flex: 1 }}>{k.label}</span>
              {count > 0 && (
                <span style={{
                  padding: '1px 7px', borderRadius: 999,
                  fontFamily: MONO, fontSize: 10,
                  background: '#0F8E6F', color: '#fff',
                }}>{count}</span>
              )}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ color: '#9A9DA1' }}>
                <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          );
        })}
      </div>
      {attachments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {attachments.map(a => (
            <div key={a.id} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 10px', fontSize: 11.5, color: '#6E7278',
            }}>
              <span style={{ fontFamily: MONO, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 9.5, color: '#9A9DA1' }}>
                {a.kind.replace(/_/g, ' ')}
              </span>
              <button
                onClick={() => onView?.(a)}
                title="View / download"
                style={{
                  flex: 1, minWidth: 0, textAlign: 'left',
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 'inherit',
                  color: '#0F1419', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  textDecoration: 'underline', textDecorationColor: '#D5D3CD',
                }}
              >
                {(a.path || '').split('/').pop()}
              </button>
              {a.batch_number && (
                <span title="Batch" style={{
                  fontFamily: MONO, fontSize: 9.5, padding: '1px 6px', borderRadius: 999,
                  background: '#EEF2F1', color: '#0F8E6F', whiteSpace: 'nowrap',
                }}>batch {a.batch_number}</span>
              )}
              <span style={{ fontFamily: MONO, fontSize: 10, color: '#9A9DA1', whiteSpace: 'nowrap' }}>
                {fmtRelative(a.uploaded_at)}
              </span>
              <button
                onClick={() => onDelete?.(a)}
                disabled={busy}
                title="Delete"
                aria-label="Delete attachment"
                style={{
                  background: 'none', border: 'none', cursor: busy ? 'default' : 'pointer',
                  color: '#9A9DA1', padding: '2px 4px', lineHeight: 1,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PaidToggle({ paid, busy, onToggle }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 16px', borderRadius: 10, marginBottom: 16,
      background: paid ? 'rgba(15,142,111,0.06)' : '#fff',
      border: `1px solid ${paid ? 'rgba(15,142,111,0.4)' : '#E8E6E0'}`,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#0F1419' }}>
          {paid ? 'Paid (reference)' : 'Not marked paid'}
        </div>
        <div style={{ fontSize: 11, color: '#9A9DA1', marginTop: 2 }}>
          Quick reference only — not the Finance invoice payment status.
        </div>
      </div>
      <button
        onClick={onToggle}
        disabled={busy}
        style={{
          flex: '0 0 auto', cursor: busy ? 'default' : 'pointer',
          padding: '7px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 500,
          fontFamily: 'inherit',
          border: `1px solid ${paid ? '#E8E6E0' : 'rgba(15,142,111,0.5)'}`,
          background: paid ? '#fff' : '#0F8E6F',
          color: paid ? '#6E7278' : '#fff',
        }}
      >
        {busy ? '…' : paid ? 'Clear paid' : 'Mark paid'}
      </button>
    </div>
  );
}

function MetaBlock({ orderedQty, supplier, eta, poRef, unitCost, poValue }) {
  const rows = [
    { label: 'Qty ordered', value: orderedQty.toLocaleString() },
    { label: 'Supplier',    value: supplier || '—' },
    { label: 'ETA',         value: fmtShortDate(eta) },
    { label: 'PO ref',      value: poRef || '—' },
    { label: 'Unit cost',   value: unitCost != null ? fmtMoney(unitCost) : '—' },
    { label: 'PO value',    value: poValue != null ? fmtMoney(poValue) : '—' },
  ];
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ ...labelStyle, marginBottom: 8 }}>PO details</div>
      <div style={{
        padding: '14px 16px', borderRadius: 10,
        background: 'rgba(15,20,25,0.025)',
        border: '1px dashed #E8E6E0',
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px 14px',
      }}>
        {rows.map(r => (
          <div key={r.label}>
            <div style={miniLabelStyle}>{r.label}</div>
            <div style={{
              fontSize: 12.5, color: '#0F1419',
              fontVariantNumeric: 'tabular-nums', marginTop: 2,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{r.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SkuActivityHistory ──────────────────────────────────────────────
// Compact per-SKU activity feed. Pulls forecaster_activity rows that
// mention this SKU plus the typed stock_adjustments ledger, merges
// and sorts. The master timeline (price history + everything else)
// stays the Catalogue drawer's responsibility — this is the
// always-visible pointer-to-the-truth on every SKU surface.
function SkuActivityHistory({ sku, cat }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sku?.id || !sku?.sku_name) { setLoading(false); return; }
    let alive = true;
    (async () => {
      const [actRes, adjRes] = await Promise.all([
        aws.from('forecaster_activity')
          .select('*')
          .ilike('detail', `%${sku.sku_name}%`)
          .order('created_at', { ascending: false }).limit(20),
        aws.from('forecaster_stock_adjustments')
          .select('*').eq('sku_id', sku.id)
          .order('created_at', { ascending: false }).limit(20),
      ]);
      if (!alive) return;
      // Drop the free-text mirror rows that duplicate stock_adjustment
      // entries (stockWrites.js writes both — the typed row IS the truth).
      const adjs = (adjRes.data || []).map(r => ({
        kind: 'adj', key: 'j-' + r.id, ts: r.created_at, who: r.actor_name,
        label: `Stock ${r.reason}`,
        detail: `${r.delta > 0 ? '+' : ''}${r.delta} → ${r.new_onhand}${r.note ? ' · ' + r.note : ''}${r.po_ref ? ' (PO ' + r.po_ref + ')' : ''}`,
      }));
      const adjStamps = new Set(adjs.map(a => a.ts?.slice(0, 16)));
      const acts = (actRes.data || [])
        .filter(r => !(r.action === 'Stock adjusted' && adjStamps.has(r.created_at?.slice(0, 16))))
        .map(r => ({
          kind: 'act', key: 'a-' + r.id, ts: r.created_at, who: r.actor_name,
          label: r.action, detail: r.detail,
        }));
      const merged = [...adjs, ...acts].sort((x, y) => (y.ts || '').localeCompare(x.ts || ''));
      setRows(merged.slice(0, 14));
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [sku?.id, sku?.sku_name]);

  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <span style={labelStyle}>Activity history</span>
        <span style={{ flex: 1 }} />
        <span style={miniLabelStyle}>Master timeline · Catalogue drawer</span>
      </div>
      <div style={{
        background: '#fff', border: '1px solid #E8E6E0', borderRadius: 10,
        padding: rows.length === 0 ? '14px 16px' : '10px 4px 4px',
      }}>
        {loading ? (
          <div style={{ fontSize: 12, color: '#9A9DA1' }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ fontSize: 12.5, color: '#9A9DA1' }}>
            No activity logged for this SKU yet. New edits and disputes will appear here.
          </div>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column' }}>
            {rows.map(r => (
              <li key={r.key} style={{
                display: 'grid', gridTemplateColumns: '8px 1fr',
                gap: 10, alignItems: 'flex-start',
                padding: '8px 12px',
                borderTop: '1px solid rgba(15,20,25,0.04)',
              }}>
                <span aria-hidden="true" style={{
                  width: 6, height: 6, borderRadius: 999, marginTop: 6,
                  background: r.kind === 'adj' ? '#0F8E6F' : '#9A9DA1',
                }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, color: '#0F1419', fontWeight: 500 }}>{r.label}</span>
                    <span style={{ fontFamily: MONO, fontSize: 10, color: '#9A9DA1', letterSpacing: '0.04em' }}>
                      {fmtRelative(r.ts)}{r.who ? ' · ' + r.who : ''}
                    </span>
                  </div>
                  {r.detail && (
                    <div style={{ fontSize: 12.5, color: '#6E7278', marginTop: 2, lineHeight: 1.4 }}>
                      {r.detail}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── EditDetailsBlock ─────────────────────────────────────────────────
// Inline edit panel for the row's editable fields. Save commits
// via authWrite and writes a diff into forecaster_activity so every
// change is in the audit trail (mirrors stockWrites.js pattern —
// activity is bare aws.from(); ARM-pinned writes go via authWrite).
const EDITABLE_FIELDS = [
  // [key, label, type, kind]
  ['qty',                  'PO Qty',           'number',     'qty'],
  ['expected_date',        'ETA',              'date',       'date'],
  ['po_ref',               'PO Ref',           'text',       'text'],
  ['skutopia_transfer_id', 'TR No',            'text',       'text'],
  ['packing_slip_number',  'Packing Slip No',  'text',       'text'],
  ['packing_slip_qty',     'PS Qty',           'number',     'qty'],
  ['delivered_qty',        'Delivered Qty',    'number',     'qty'],
  ['batch_number',         'Batch',            'text',       'text'],
  ['expiry_date',          'Expiry',           'date',       'date'],
  ['warehouse_location',   'Warehouse',        'text',       'text'],
  ['notes',                'Notes',            'textarea',   'text'],
];

function EditDetailsBlock({ session, po, sku, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(() => seedDraft(po));
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState('');

  // Reseed when the underlying row identity changes (drawer reused
  // for a different PO).
  useEffect(() => { setDraft(seedDraft(po)); setEditing(false); setErr(''); }, [po?.id]);

  function field(k, v) { setDraft(d => ({ ...d, [k]: v })); }
  function reset() { setDraft(seedDraft(po)); setEditing(false); setErr(''); }

  async function save() {
    setBusy(true);
    setErr('');
    try {
      const patch = {};
      const changes = [];
      for (const [key, label, , kind] of EDITABLE_FIELDS) {
        const before = po[key];
        const after  = normaliseDraftValue(draft[key], kind);
        // Stringify both sides for stable equality (handles "" vs null,
        // numeric strings vs numbers).
        const bs = before == null ? '' : String(before);
        const as = after  == null ? '' : String(after);
        if (bs === as) continue;
        patch[key] = after;
        changes.push(`${label}: ${bs || '—'} → ${as || '—'}`);
      }
      if (changes.length === 0) { setEditing(false); setBusy(false); return; }

      await authWrite(session, 'forecaster_incoming_stock', 'update', patch,
        { column: 'id', value: po.id });

      // Bare insert — forecaster_activity IS the audit log.
      await aws.from('forecaster_activity').insert([{
        actor_name: session?.name,
        action: 'Incoming edited',
        detail: `${sku?.sku_name || po.sku_id}${po.po_ref ? ' (PO ' + po.po_ref + ')' : ''}: ${changes.join(' · ')}`,
      }]);

      setEditing(false);
      onSaved?.();
    } catch (e) {
      console.error('[IncomingDrawer] edit failed', e);
      setErr(e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <span style={labelStyle}>Edit details</span>
        <span style={{ flex: 1 }} />
        {editing ? (
          <>
            <button type="button" onClick={reset} disabled={busy} style={ghostBtn}>Cancel</button>
            <button type="button" onClick={save}  disabled={busy} style={primaryBtn}>{busy ? 'Saving…' : 'Save'}</button>
          </>
        ) : (
          <button type="button" onClick={() => setEditing(true)} style={ghostBtn}>Edit</button>
        )}
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
        background: '#fff', border: '1px solid #E8E6E0', borderRadius: 10,
        padding: '14px 16px',
      }}>
        {EDITABLE_FIELDS.map(([key, label, type]) => (
          <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: type === 'textarea' ? '1 / -1' : 'auto' }}>
            <span style={miniLabelStyle}>{label}</span>
            {editing ? (
              type === 'textarea' ? (
                <textarea
                  value={draft[key] ?? ''}
                  onChange={e => field(key, e.target.value)}
                  rows={2}
                  style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
                />
              ) : (
                <input
                  type={type}
                  value={draft[key] ?? ''}
                  onChange={e => field(key, e.target.value)}
                  style={inputStyle}
                />
              )
            ) : (
              <span style={{ fontSize: 13, color: po[key] == null || po[key] === '' ? '#9A9DA1' : '#0F1419' }}>
                {formatReadOnly(po[key], type)}
              </span>
            )}
          </div>
        ))}
      </div>
      {err && <div style={{ marginTop: 6, color: '#9A4820', fontSize: 12 }}>{err}</div>}
    </div>
  );
}
function seedDraft(po) {
  const d = {};
  for (const [key] of EDITABLE_FIELDS) {
    const v = po?.[key];
    d[key] = v == null ? '' : String(v);
  }
  return d;
}
function normaliseDraftValue(v, kind) {
  if (v == null || v === '') return null;
  if (kind === 'qty') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return String(v);
}
function formatReadOnly(v, type) {
  if (v == null || v === '') return '—';
  if (type === 'date') return fmtShortDate(v);
  if (type === 'number') return Number(v).toLocaleString();
  return String(v);
}
const ghostBtn = {
  background: 'transparent', color: '#6E7278',
  border: '1px solid rgba(110,114,120,0.3)',
  borderRadius: 8, padding: '4px 12px', fontSize: 11, fontWeight: 500, cursor: 'pointer',
};
const primaryBtn = {
  background: '#0F1419', color: '#F4EFE6',
  border: '1px solid #0F1419',
  borderRadius: 8, padding: '4px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
};

const labelStyle = {
  fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.14em',
  textTransform: 'uppercase', color: '#6E7278', fontWeight: 500,
};
const miniLabelStyle = {
  fontFamily: MONO, fontSize: 8.5, letterSpacing: '0.12em',
  textTransform: 'uppercase', color: '#9A9DA1',
};
const inputStyle = {
  padding: '8px 12px',
  border: '1px solid #E8E6E0', borderRadius: 7,
  background: '#fff', fontSize: 13, color: '#0F1419',
  fontFamily: 'inherit', outline: 'none', width: '100%',
};
const emptyStyle = {
  color: '#9A9DA1', fontSize: 13, padding: '20px 0', textAlign: 'center',
};
